/**
 * WhatsApp Opt-Out (Do Not Blast) TanStack Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  waApi,
  type WAOptOutSearchResponse,
  type WAOptOutListItem,
  type WAOptOutInput,
} from '@/lib/api-client'

export const waOptoutKeys = {
  all: ['wa-optouts'] as const,
  list: () => [...waOptoutKeys.all, 'list'] as const,
  search: (q: string) => [...waOptoutKeys.all, 'search', q] as const,
}

export function useOptOutSearch(q: string) {
  return useQuery<WAOptOutSearchResponse>({
    queryKey: waOptoutKeys.search(q),
    queryFn: () => waApi.optout.search(q),
    enabled: q.trim().length >= 2,
    staleTime: 10 * 1000,
  })
}

export function useOptOutList() {
  return useQuery<{ data: WAOptOutListItem[]; total: number }>({
    queryKey: waOptoutKeys.list(),
    queryFn: () => waApi.optout.list(),
    staleTime: 30 * 1000,
  })
}

export function useOptOutContact() {
  const queryClient = useQueryClient()
  return useMutation<{ success: boolean }, Error, WAOptOutInput>({
    mutationFn: (data) => waApi.optout.optOut(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: waOptoutKeys.all })
    },
  })
}

export function useRestoreContact() {
  const queryClient = useQueryClient()
  return useMutation<{ success: boolean }, Error, { contactId: number; notes?: string }>({
    mutationFn: ({ contactId, notes }) => waApi.optout.restore(contactId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: waOptoutKeys.all })
    },
  })
}
